// Dry media for the Pencil tool: mechanical pencil, coloured pencil, charcoal,
// soft pastel, chalk and wax crayon. (Graphite keeps its original tip-texture
// stamping in canvasGpuCore and does not use this module.)
//
// Every medium deposits through one GPU shader with a material model per
// medium. The pencil tool's stamps give the path, pressure and spacing; this
// module turns each stamp into the medium's own contact footprint (fine point,
// round tip, stick laid across the stroke, blunt wax tip), oriented to the
// stroke. The fragment shader then works out, per pixel:
//   - the footprint edge (crisp, powdery, jagged or blunt),
//   - the material texture, from noise in *document* space and stroke-space
//     streak coordinates, so overlapping stamps reinforce one coherent pattern
//     instead of repeating a stamp image,
//   - contact with the paper tooth (surfaceContact.js), with real pressure.
// Stamps of one draw call are batched into a single draw.

import { SURFACE_CONTACT_GLSL } from '../../../canvas/paint-engine/integration/surfaceContact';

export const DRY_MEDIA = Object.freeze(['mechanical', 'coloured', 'charcoal', 'pastel', 'chalk', 'crayon']);
export const isDryMedium = (id) => DRY_MEDIA.includes(id);

// Footprint and deposition per medium.
//   radius:   footprint half-width as a share of the tool size (minor axis)
//   aspect:   long axis / short axis (sticks are laid across the stroke)
//   tiltAspect: extra length when the stylus is tilted (side contact)
//   pressureSize: how much pressure widens the contact
//   coverage: [light, heavy] coverage of one pass
//   minRadius: smallest radius in px
export const MEDIUM_GEOMETRY = Object.freeze({
  mechanical: { id: 0, radius: 0.16, aspect: 1, tiltAspect: 0.15, pressureSize: 0.08, coverage: [0.45, 0.85], minRadius: 0.55 },
  coloured: { id: 1, radius: 0.34, aspect: 1.05, tiltAspect: 2.2, pressureSize: 0.3, coverage: [0.22, 0.78], minRadius: 0.9 },
  charcoal: { id: 2, radius: 0.46, aspect: 1.2, tiltAspect: 3.2, pressureSize: 0.4, coverage: [0.32, 0.92], minRadius: 1.2 },
  pastel: { id: 3, radius: 0.58, aspect: 1.7, tiltAspect: 1.6, pressureSize: 0.25, coverage: [0.42, 0.95], minRadius: 2 },
  chalk: { id: 4, radius: 0.52, aspect: 1.7, tiltAspect: 1.4, pressureSize: 0.2, coverage: [0.26, 0.72], minRadius: 1.6 },
  crayon: { id: 5, radius: 0.42, aspect: 1.25, tiltAspect: 1.0, pressureSize: 0.15, coverage: [0.3, 0.9], minRadius: 1.4 },
});

// Quad margin around the footprint (irregular edges and loose particles).
const MARGIN = 1.45;

export const DRY_MEDIA_VERTEX = `
  attribute vec2 a_position;
  attribute vec2 a_local;
  attribute vec4 a_stamp;
  attribute vec2 a_dir;
  uniform vec2 u_resolution;
  varying vec2 v_documentPosition;
  varying vec2 v_local;
  varying vec4 v_stamp;
  varying vec2 v_dir;
  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    gl_Position = vec4(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0, 0.0, 1.0);
    v_documentPosition = a_position;
    v_local = a_local;
    v_stamp = a_stamp;
    v_dir = a_dir;
  }
`;

// v_stamp: x = deposit per stamp, y = pressure, z = stroke seed (0..1), w = minor radius in px.
// v_local: footprint coordinates, |v_local| = 1 on the nominal edge; x runs along
// the stick's long axis (across the stroke), y along the stroke.
export const DRY_MEDIA_FRAGMENT = `
  precision highp float;
  uniform vec3 u_colour;
  uniform float u_medium;
  uniform float u_density;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_documentPosition;
  varying vec2 v_local;
  varying vec4 v_stamp;
  varying vec2 v_dir;
  ${SURFACE_CONTACT_GLSL}

  float vxHash(vec2 p) {
    p = fract(p * vec2(0.3183099, 0.3678794) + vec2(0.71, 0.113));
    p += dot(p, p.yx + 19.19);
    return fract(p.x * p.y * 95.4337);
  }
  float vxNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
    float a = vxHash(i);
    float b = vxHash(i + vec2(1.0, 0.0));
    float c = vxHash(i + vec2(0.0, 1.0));
    float d = vxHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
  // Fractal noise with rotated octaves (no axis-aligned structure).
  float vxFbm(vec2 p) {
    float sum = 0.0;
    float amp = 0.5;
    mat2 turn = mat2(0.8, -0.6, 0.6, 0.8);
    for (int k = 0; k < 4; k++) {
      sum += amp * vxNoise(p);
      p = turn * p * 2.03 + vec2(17.3, 9.1);
      amp *= 0.5;
    }
    return sum / 0.9375;
  }
  // Paper height (standard deviations) and a medium-specific reading of the
  // tooth: the same reach as surfaceContact, with the contact softness scaled.
  // Wide ramps (pastel) let pigment sit over and into the grain; narrow ones
  // (crayon) skip the valleys. Paper Influence / visual-only via u_toothStrength.
  float vxToothZ(vec2 p) {
    return (texture2D(u_tooth, p / u_toothResolution).r - 0.5) * 6.0;
  }
  float vxToothRamp(vec2 p, float pressure, float widthScale) {
    if (u_toothStrength <= 0.0005) return 1.0;
    float reach = u_contact.x + u_contact.y * clamp(pressure, 0.0, 1.0);
    float w = u_contact.z * widthScale;
    float touch = smoothstep(-w, w, reach - (2.0 - vxToothZ(p)));
    return mix(1.0, u_contact.w + (1.0 - u_contact.w) * touch, clamp(u_toothStrength, 0.0, 1.0));
  }
  float vxNoise1(float x) {
    float i = floor(x);
    float f = fract(x);
    float u = f * f * (3.0 - 2.0 * f);
    return mix(vxHash(vec2(i, 3.7)), vxHash(vec2(i + 1.0, 3.7)), u);
  }

  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }
    vec2 P = v_documentPosition;
    float pressure = v_stamp.y;
    float seed = v_stamp.z * 97.0;
    float radiusPx = max(0.5, v_stamp.w);
    vec2 dir = normalize(v_dir + vec2(1e-6, 0.0));
    vec2 across = vec2(-dir.y, dir.x);
    // Stroke-space coordinates in document px: streaks run along the stroke.
    float s = dot(P, across);
    float along = dot(P, dir);
    float r = length(v_local);
    float contact = surfaceContact(P, pressure);
    float deposit = 0.0;

    if (u_medium < 0.5) {
      // Mechanical pencil: a fine, hard point. Crisp anti-aliased edge, even
      // graphite with only a faint micro-texture; the tooth barely matters.
      float aa = 0.9 / radiusPx;
      float edge = 1.0 - smoothstep(1.0 - aa, 1.0 + aa, r);
      deposit = edge * (0.94 + 0.06 * vxNoise(P * 0.9)) * contact;
    } else if (u_medium < 1.5) {
      // Coloured pencil: wax/oil-bound pigment. Soft round tip, fine waxy
      // streaks along the stroke; pressure (burnishing) fills the streaks and
      // the tooth so heavy layers turn smooth and saturated.
      float edge = 1.0 - smoothstep(0.78, 1.0 + 0.7 / radiusPx, r);
      // Wax streaks about 2-3 px apart, running with the stroke.
      float streak = vxNoise1(s * 0.38 + seed) * 0.55 + vxNoise(P * 0.5) * 0.45;
      float burnish = smoothstep(0.6, 1.0, pressure);
      float wax = mix(0.45 + 0.55 * smoothstep(0.3, 0.7, streak), 1.0, burnish * 0.8);
      float tooth = vxToothRamp(P, pressure, 1.2);
      deposit = edge * wax * mix(tooth, 1.0, burnish * 0.55) * (0.9 + 0.1 * vxNoise(P * 1.2 + seed));
    } else if (u_medium < 2.5) {
      // Charcoal: granular, crumbly. Irregular edge, particle grain that opens
      // up at light pressure, loose specks around the mark; u_density moves
      // from vine (light, broken) to compressed (dense, velvety black).
      float wobble = (vxFbm(P * 0.11 + seed) - 0.5) * 0.45;
      float edge = 1.0 - smoothstep(0.55, 1.05, r + wobble);
      // The tooth sets how densely particles settle; the particles themselves
      // are the charcoal's own fine grain (about 1-2 px).
      float gate = vxToothRamp(P, pressure, 1.6);
      float grain = 0.55 * vxNoise(P * 0.55 + vec2(seed, 1.7)) + 0.45 * vxFbm(P * 0.3 + vec2(seed, 0.0));
      float fill = clamp(gate * (0.3 + 0.6 * pressure) + u_density * 0.2, 0.0, 1.0);
      float particles = smoothstep(1.0 - fill - 0.14, 1.0 - fill + 0.14, grain);
      float body = mix(particles, 1.0, u_density * 0.35 * pressure * pressure);
      deposit = edge * body;
      // Loose particles just outside the mark.
      float ring = smoothstep(0.85, 1.05, r) * (1.0 - smoothstep(1.05, 1.4, r));
      float speck = step(0.93, vxHash(floor(P * 0.9) + seed));
      deposit = max(deposit, ring * speck * 0.55);
    } else if (u_medium < 3.5) {
      // Soft pastel: a broad stick laid across the stroke. Velvety, soft
      // powdery edge; pigment sits over the grain (wide contact) and drifts
      // into the valleys as loose powder; broad areas build up smooth colour.
      vec2 q = abs(v_local);
      float stick = pow(pow(q.x, 3.0) + pow(q.y, 3.0), 1.0 / 3.0);
      float powderEdge = 0.55 + 0.3 * vxFbm(P * 0.33 + seed);
      float edge = 1.0 - smoothstep(powderEdge, 1.05, stick);
      float velvet = 0.84 + 0.16 * vxFbm(P * 0.045);
      float powder = 0.8 + 0.2 * vxNoise(P * 1.1 + seed);
      // Pigment sits on the grain and drifts into the valleys (wide, soft ramp).
      float tooth = vxToothRamp(P, pressure, 2.0);
      deposit = edge * velvet * powder * (0.3 + 0.7 * tooth);
    } else if (u_medium < 4.5) {
      // Chalk: brittle mineral stick. Jagged contact edge, skipping bands
      // along the stroke where the stick chatters, dusty light speckle.
      vec2 q = abs(v_local);
      float stick = max(q.x, q.y * 1.1);
      float jag = (vxNoise(P * 0.5 + seed) - 0.5) * 0.4;
      float edge = 1.0 - smoothstep(0.7, 0.95, stick + jag);
      // The brittle stick chatters: skips come and go along the direction of travel.
      float chatter = smoothstep(0.25, 0.55, vxNoise1(along * 0.09 + seed) * 0.6 + vxNoise(P * 0.09) * 0.4);
      float gate = vxToothRamp(P, pressure, 1.3);
      float grit = 0.65 * vxNoise(P * 0.85 + vec2(seed, 4.1)) + 0.35 * vxNoise(P * 2.1 + seed);
      float bite = smoothstep(1.0 - gate * mix(0.35, 1.0, chatter) - 0.12, 1.0 - gate * mix(0.35, 1.0, chatter) + 0.12, grit);
      deposit = edge * bite * (0.75 + 0.25 * vxHash(floor(P) + seed));
      float haze = (1.0 - smoothstep(0.9, 1.3, stick)) * 0.12 * vxNoise(P * 1.3);
      deposit = max(deposit, haze);
    } else {
      // Wax crayon: blunt, irregular wax tip. Strong parallel wax streaks,
      // wax skips the valleys at light pressure and fills them as pressure and
      // passes build up; edges are blunt with small wax crumbs.
      float wobble = (vxNoise(P * 0.2 + seed) - 0.5) * 0.25;
      float edge = 1.0 - smoothstep(0.78, 0.98, r + wobble);
      // Wax streaks along the stroke dominate (two scales of the tip's ridges).
      float ridges = vxNoise1(s * 0.42 + seed * 3.0) * 0.65 + vxNoise1(s * 1.0 + seed * 5.0) * 0.25 + vxNoise(P * vec2(0.35)) * 0.1;
      float streak = smoothstep(0.32, 0.68, ridges);
      float fill = smoothstep(0.4, 1.0, pressure);
      float tooth = vxToothRamp(P, pressure, 1.3);
      deposit = edge * mix(0.22 + 0.78 * streak, 1.0, fill * 0.55) * mix(tooth, 1.0, fill * 0.4);
      float crumb = (1.0 - smoothstep(0.98, 1.2, r)) * step(0.988, vxHash(floor(P * 0.7) + seed)) * 0.25;
      deposit = max(deposit, crumb);
    }

    float alpha = clamp(deposit * v_stamp.x, 0.0, 1.0);
    if (alpha <= 0.0008) discard;
    gl_FragColor = vec4(u_colour * alpha, alpha);
  }
`;

const FLOATS_PER_VERTEX = 10; // position 2, local 2, stamp 4, dir 2

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function pressureOf(stamp) {
  const p = Number(stamp && (stamp.pressure ?? (stamp.point && stamp.point.pressure)));
  return Number.isFinite(p) && p > 0 ? clamp(p, 0.02, 1) : 0.6;
}

// Pencil stamps -> vertex data for one medium. state carries the stroke
// direction between calls. Returns { vertices, count, bounds }.
export function buildDryMediaVertices(stamps, { medium, size = 8, opacity = 1, tilt = 0 }, state = {}) {
  const geometry = MEDIUM_GEOMETRY[medium] || MEDIUM_GEOMETRY.mechanical;
  const toolSize = Math.max(1, Number(size) || 1);
  const tiltAmount = clamp(Number(tilt) || 0, 0, 1);
  const out = new Float32Array(stamps.length * 6 * FLOATS_PER_VERTEX);
  let offset = 0;
  let count = 0;
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
  const seed = ((Number(state.seed) || 0) % 997) / 997;
  for (let k = 0; k < stamps.length; k += 1) {
    const stamp = stamps[k];
    const x = Number(stamp && stamp.point && stamp.point.x);
    const y = Number(stamp && stamp.point && stamp.point.y);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    // Stroke direction from the previous stamp (kept across calls).
    if (state.last) {
      const dx = x - state.last.x;
      const dy = y - state.last.y;
      const len = Math.hypot(dx, dy);
      if (len > 0.25) {
        const nx = dx / len; const ny = dy / len;
        // Smooth the direction so curves stay coherent.
        const sx = (state.dirX ?? nx) * 0.6 + nx * 0.4;
        const sy = (state.dirY ?? ny) * 0.6 + ny * 0.4;
        const sl = Math.hypot(sx, sy) || 1;
        state.dirX = sx / sl; state.dirY = sy / sl;
        state.spacing = state.spacing ? state.spacing * 0.8 + len * 0.2 : len;
      }
    }
    state.last = { x, y };
    const dirX = state.dirX ?? 1;
    const dirY = state.dirY ?? 0;
    const pressure = pressureOf(stamp);
    const minor = Math.max(geometry.minRadius, toolSize * geometry.radius * (1 - geometry.pressureSize + 2 * geometry.pressureSize * pressure));
    const major = minor * (geometry.aspect + geometry.tiltAspect * tiltAmount);
    // One pass reaches the medium's coverage for this pressure; each pixel is
    // covered by about (2 * minor / spacing) overlapping stamps.
    const spacing = Math.max(0.2, state.spacing || toolSize * 0.1);
    const overlaps = Math.max(1, (2 * minor) / spacing);
    const target = clamp((geometry.coverage[0] + (geometry.coverage[1] - geometry.coverage[0]) * pressure) * clamp(opacity, 0.01, 1), 0.001, 0.98);
    const perStamp = 1 - (1 - target) ** (1 / overlaps);
    // Long axis across the stroke (sticks, side contact); short axis along it.
    const ax = -dirY; const ay = dirX;
    const hx = major * MARGIN; const hy = minor * MARGIN;
    const corners = [[-1, -1], [1, -1], [-1, 1], [-1, 1], [1, -1], [1, 1]];
    for (const [cx, cy] of corners) {
      const lx = cx * hx; const ly = cy * hy;
      const px = x + ax * lx + dirX * ly;
      const py = y + ay * lx + dirY * ly;
      out[offset] = px; out[offset + 1] = py;
      out[offset + 2] = lx / major; out[offset + 3] = ly / minor;
      out[offset + 4] = perStamp; out[offset + 5] = pressure; out[offset + 6] = seed; out[offset + 7] = minor;
      out[offset + 8] = dirX; out[offset + 9] = dirY;
      offset += FLOATS_PER_VERTEX;
      if (px < minX) minX = px; if (py < minY) minY = py; if (px > maxX) maxX = px; if (py > maxY) maxY = py;
    }
    count += 6;
  }
  return { vertices: out.subarray(0, offset), count, bounds: { minX, minY, maxX, maxY } };
}

// Charcoal density: pencil grade H (vine, light) .. B (compressed, dense).
export function mediumDensity(medium, grade = 0) {
  const g = clamp(Number(grade) || 0, -1, 1);
  if (medium === 'charcoal') return clamp(0.45 + 0.5 * g, 0, 1);
  return 0.5;
}

export function createDryMediaRenderer(gl, createProgram) {
  const program = createProgram(gl, DRY_MEDIA_VERTEX, DRY_MEDIA_FRAGMENT);
  const buffer = gl.createBuffer();
  const loc = {
    position: gl.getAttribLocation(program, 'a_position'),
    local: gl.getAttribLocation(program, 'a_local'),
    stamp: gl.getAttribLocation(program, 'a_stamp'),
    dir: gl.getAttribLocation(program, 'a_dir'),
  };
  return {
    program,
    // Caller binds the framebuffer, blending and the shared uniforms
    // (selection, tooth) before drawing.
    draw(vertexData, count, { colour, medium, density, width, height }) {
      if (!count) return;
      gl.useProgram(program);
      gl.uniform2f(gl.getUniformLocation(program, 'u_resolution'), width, height);
      gl.uniform3f(gl.getUniformLocation(program, 'u_colour'), colour[0], colour[1], colour[2]);
      gl.uniform1f(gl.getUniformLocation(program, 'u_medium'), (MEDIUM_GEOMETRY[medium] || MEDIUM_GEOMETRY.mechanical).id);
      gl.uniform1f(gl.getUniformLocation(program, 'u_density'), density);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, vertexData, gl.STREAM_DRAW);
      const stride = FLOATS_PER_VERTEX * 4;
      const attrib = (location, size, offset) => {
        if (location < 0) return;
        gl.enableVertexAttribArray(location);
        gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset * 4);
      };
      attrib(loc.position, 2, 0);
      attrib(loc.local, 2, 2);
      attrib(loc.stamp, 4, 4);
      attrib(loc.dir, 2, 8);
      gl.drawArrays(gl.TRIANGLES, 0, count);
      [loc.position, loc.local, loc.stamp, loc.dir].forEach((l) => { if (l >= 0) gl.disableVertexAttribArray(l); });
    },
    destroy() {
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    },
  };
}
